import { Bot, Check, ChevronRight, Code2, Database, Rocket, Sparkles, WandSparkles, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStudio } from "../studioStore";
import type { StudioView } from "../types";
import { BLACKED_ONBOARDING_KEY, BLACKED_START_EVENT } from "./blackedEvents";

type CreationKind = "website" | "system";

function hasFinishedOnboarding() {
  try { return localStorage.getItem(BLACKED_ONBOARDING_KEY) === "complete"; }
  catch { return false; }
}

export function BlackedOnboarding() {
  const { t } = useTranslation();
  const { addComponent, addTable, addWorkflow, logActivity, setView } = useStudio();
  const [open, setOpen] = useState(() => !hasFinishedOnboarding());
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<CreationKind>("website");
  const [name, setName] = useState("");
  const [created, setCreated] = useState(false);

  useEffect(() => {
    const restart = () => { setStep(0); setCreated(false); setOpen(true); };
    window.addEventListener(BLACKED_START_EVENT, restart);
    return () => window.removeEventListener(BLACKED_START_EVENT, restart);
  }, []);

  const journey = useMemo(() => kind === "system"
    ? ["designer", "data", "assistant", "deploy"] as StudioView[]
    : ["designer", "assistant", "deploy"] as StudioView[], [kind]);

  function persistComplete() {
    try { localStorage.setItem(BLACKED_ONBOARDING_KEY, "complete"); } catch { /* Disponible en la sesión actual. */ }
  }

  function close() { persistComplete(); setOpen(false); }

  function makeComponent(item: Omit<Parameters<typeof addComponent>[0], "id">) {
    return { ...item, id: crypto.randomUUID() };
  }

  function createStarter() {
    if (created) return;
    const creationName = name.trim() || t(`blacked.defaults.${kind}`);
    const shared = [
      makeComponent({ kind: "hero", name: t("blacked.blocks.heroName"), title: creationName, body: t(`blacked.blocks.${kind}Hero`), actionLabel: t("blacked.blocks.cta"), alignment: "left", tone: "accent" }),
      makeComponent({ kind: "cards", name: t("blacked.blocks.cardsName"), title: t("blacked.blocks.cardsTitle"), body: t(`blacked.blocks.${kind}Cards`), columns: 3, tone: "default" }),
      makeComponent({ kind: "form", name: t("blacked.blocks.formName"), title: t("blacked.blocks.formTitle"), body: t("blacked.blocks.formBody"), actionLabel: t("blacked.blocks.formAction"), tone: "muted" }),
      makeComponent({ kind: "footer", name: t("blacked.blocks.footerName"), title: creationName, body: t("blacked.blocks.footerBody"), tone: "dark" }),
    ];
    shared.forEach(addComponent);

    if (kind === "system") {
      const tableId = crypto.randomUUID();
      addTable({ id: tableId, name: `items_${Date.now().toString(36)}`, label: t("blacked.system.tableLabel"), writeRole: "editor", fields: [
        { id: crypto.randomUUID(), name: "id", type: "integer", required: true, primary: true },
        { id: crypto.randomUUID(), name: "name", type: "text", required: true },
        { id: crypto.randomUUID(), name: "status", type: "text", required: true },
      ] });
      addWorkflow({ id: crypto.randomUUID(), name: t("blacked.system.workflowName"), method: "POST", route: `/api/items-${Date.now().toString(36)}`, actions: [
        { id: crypto.randomUUID(), kind: "input", label: t("blacked.system.receive"), detail: "name, status", enabled: true },
        { id: crypto.randomUUID(), kind: "validate", label: t("blacked.system.validate"), detail: "name required", enabled: true },
        { id: crypto.randomUUID(), kind: "response", label: t("blacked.system.respond"), detail: "{ ok: true }", enabled: true },
      ] });
    }
    logActivity(t("blacked.activityTitle"), t("blacked.activityBody", { name: creationName }), "success");
    setCreated(true);
    setStep(2);
  }

  function openJourneyView(index: number) {
    setView(journey[index]);
    setStep(3 + index);
  }

  if (!open) return (
    <button className="blacked-launcher" onClick={() => setOpen(true)} aria-label={t("blacked.open")} title={t("blacked.open")}>
      <Bot size={22}/><span>blacked</span>
    </button>
  );

  const journeyIndex = Math.max(0, step - 3);
  const isJourney = step >= 3;
  const isLastJourney = isJourney && journeyIndex === journey.length - 1;

  return <aside className="blacked-panel" role="dialog" aria-modal="false" aria-labelledby="blacked-title">
    <header className="blacked-header">
      <div className="blacked-avatar" aria-hidden="true"><Bot size={24}/><Sparkles size={12}/></div>
      <div><small>{t("blacked.kicker")}</small><strong>blacked</strong></div>
      <button onClick={close} aria-label={t("blacked.close")}><X size={17}/></button>
    </header>

    <div className="blacked-progress" aria-label={t("blacked.progress")}><span style={{ width: `${Math.min(100, ((step + 1) / (journey.length + 3)) * 100)}%` }}/></div>

    <div className="blacked-body">
      {step === 0 && <>
        <span className="blacked-bubble">{t("blacked.hello")}</span>
        <h2 id="blacked-title">{t("blacked.welcomeTitle")}</h2>
        <p>{t("blacked.welcomeBody")}</p>
        <button className="btn primary" onClick={() => setStep(1)}>{t("blacked.letsGo")}<ChevronRight size={15}/></button>
        <button className="blacked-text-button" onClick={close}>{t("blacked.later")}</button>
      </>}

      {step === 1 && <>
        <h2 id="blacked-title">{t("blacked.chooseTitle")}</h2><p>{t("blacked.chooseBody")}</p>
        <div className="blacked-kind-grid">
          <button className={kind === "website" ? "is-active" : ""} onClick={() => setKind("website")}><Code2/><strong>{t("blacked.website")}</strong><small>{t("blacked.websiteHint")}</small></button>
          <button className={kind === "system" ? "is-active" : ""} onClick={() => setKind("system")}><Database/><strong>{t("blacked.systemOption")}</strong><small>{t("blacked.systemHint")}</small></button>
        </div>
        <label className="blacked-name">{t("blacked.nameLabel")}<input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder={t(`blacked.defaults.${kind}`)} maxLength={80}/></label>
        <button className="btn primary" onClick={createStarter}><WandSparkles size={15}/>{t("blacked.create")}</button>
      </>}

      {step === 2 && <>
        <span className="blacked-success"><Check size={18}/></span>
        <h2 id="blacked-title">{t("blacked.createdTitle")}</h2><p>{t(`blacked.createdBody.${kind}`)}</p>
        <button className="btn primary" onClick={() => openJourneyView(0)}>{t("blacked.showDesign")}<ChevronRight size={15}/></button>
      </>}

      {isJourney && <>
        <span className="blacked-step-icon">{journey[journeyIndex] === "deploy" ? <Rocket/> : journey[journeyIndex] === "data" ? <Database/> : <Sparkles/>}</span>
        <h2 id="blacked-title">{t(`blacked.journey.${journey[journeyIndex]}.title`)}</h2>
        <p>{t(`blacked.journey.${journey[journeyIndex]}.body`)}</p>
        {isLastJourney ? <button className="btn primary" onClick={close}><Check size={15}/>{t("blacked.finish")}</button> : <button className="btn primary" onClick={() => openJourneyView(journeyIndex + 1)}>{t("blacked.next")}<ChevronRight size={15}/></button>}
      </>}
    </div>
  </aside>;
}
